import { m } from '@pricedrop/i18n';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Heatmap } from '@/components/statistics/heatmap';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';

export const Route = createFileRoute('/statistics/')({
  component: StatisticsPage,
});

interface HistogramRow {
  dow: number;
  hour: number;
  newItemAdded: number;
  priceDecrease: number;
  lowestPriceDecrease: number;
  priceIncrease: number;
  highestPriceIncrease: number;
  wentOutOfStock: number;
  backInStock: number;
}

const HEATMAP_CONFIGS = [
  {
    key: 'lowestPriceDecrease' as const,
    label: () => m.statistics_lowestPriceDecrease(),
    lowColor: '#fef3c7',
    highColor: '#f59e0b',
    levels: 10,
  },
  {
    key: 'priceDecrease' as const,
    label: () => m.statistics_priceDecrease(),
    lowColor: '#f3e8ff',
    highColor: '#7c3aed',
    levels: 20,
  },
  {
    key: 'highestPriceIncrease' as const,
    label: () => m.statistics_highestPriceIncrease(),
    lowColor: '#ecfccb',
    highColor: '#65a30d',
    levels: 30,
  },
  {
    key: 'priceIncrease' as const,
    label: () => m.statistics_priceIncrease(),
    lowColor: '#dcfce7',
    highColor: '#16a34a',
    levels: 20,
  },
  {
    key: 'newItemAdded' as const,
    label: () => m.statistics_newItemAdded(),
    lowColor: '#fce7f3',
    highColor: '#db2777',
    levels: 10,
  },
  {
    key: 'wentOutOfStock' as const,
    label: () => m.statistics_wentOutOfStock(),
    lowColor: '#dbeafe',
    highColor: '#2563eb',
    levels: 10,
  },
  {
    key: 'backInStock' as const,
    label: () => m.statistics_backInStock(),
    lowColor: '#fee2e2',
    highColor: '#dc2626',
    levels: 10,
  },
] as const;

function StatisticsPage() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['statistics', 'price-change-histogram'],
    queryFn: async () => {
      const res = await api.api.statistics['price-change-histogram'].get();
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 10 * 60 * 1000,
  });

  const rows = data?.data as HistogramRow[] | undefined;

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">{m.statistics_title()}</h1>
        <p className="text-muted-foreground mt-1">{m.statistics_subtitle()}</p>
      </div>

      {isError ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
            <p className="text-muted-foreground">{m.error_generic()}</p>
            <Button variant="outline" onClick={() => refetch()}>
              {m.error_retry()}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {HEATMAP_CONFIGS.map((config) => (
            <Card key={config.key}>
              <CardHeader>
                <CardTitle className="text-lg">{config.label()}</CardTitle>
                <CardDescription>{m.statistics_subtitle()}</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-[180px] w-full" />
                ) : rows ? (
                  <Heatmap
                    data={rows.map((row) => ({
                      dow: row.dow,
                      hour: row.hour,
                      value: row[config.key],
                    }))}
                    lowColor={config.lowColor}
                    highColor={config.highColor}
                    numLevels={config.levels}
                  />
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
